import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Mic, Image as ImageIcon, FileText, MapPin, SendHorizontal, Trash2, Square, Zap } from "lucide-react";
import { getLocation } from "@/lib/photo";

const OPTIONS = [
  { id: "audio", label: "Suara", icon: Mic, color: "bg-orange-500" },
  { id: "image", label: "Gambar", icon: ImageIcon, color: "bg-violet-600" },
  { id: "pdf", label: "PDF", icon: FileText, color: "bg-red-600" },
  { id: "location", label: "Tikor", icon: MapPin, color: "bg-emerald-600" },
];

const pickMime = () => ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus", "audio/webm"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";

const Recorder = ({ onDone, onCancel }) => {
  const [secs, setSecs] = useState(0);
  const rec = useRef(null);
  const chunks = useRef([]);
  const start = useRef(Date.now());
  const cancelled = useRef(false);

  useEffect(() => {
    let stream;
    const t = setInterval(() => setSecs(Math.floor((Date.now() - start.current) / 1000)), 250);
    navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
      stream = s;
      const mime = pickMime();
      const r = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = () => {
        s.getTracks().forEach((tr) => tr.stop());
        if (cancelled.current) return;
        const type = (r.mimeType || "audio/webm").split(";")[0];
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        onDone(new File(chunks.current, `pesan-suara.${ext}`, { type }), (Date.now() - start.current) / 1000);
      };
      r.start();
      rec.current = r;
      start.current = Date.now();
    }).catch(() => {
      toast.error("Izin mikrofon ditolak. Aktifkan mikrofon untuk mengirim suara.");
      onCancel();
    });
    return () => {
      clearInterval(t);
      if (rec.current?.state === "recording") {
        cancelled.current = true;
        rec.current.stop();
      }
      stream?.getTracks().forEach((tr) => tr.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stop = (cancel) => {
    cancelled.current = cancel;
    if (rec.current?.state === "recording") rec.current.stop();
    if (cancel) onCancel();
  };

  return (
    <div className="flex items-center gap-3 flex-1 bg-white rounded-full px-4 h-11" data-testid="chat-recorder">
      <span className="h-2.5 w-2.5 rounded-full bg-red-600 animate-pulse" />
      <span className="text-sm font-mono text-slate-700 flex-1" data-testid="chat-recorder-time">{String(Math.floor(secs / 60)).padStart(2, "0")}:{String(secs % 60).padStart(2, "0")}</span>
      <button onClick={() => stop(true)} data-testid="chat-recorder-cancel" className="p-1.5 text-slate-500 hover:text-red-600" aria-label="Batal"><Trash2 className="h-5 w-5" /></button>
      <button onClick={() => stop(false)} data-testid="chat-recorder-send" className="h-9 w-9 rounded-full bg-emerald-600 text-white flex items-center justify-center" aria-label="Kirim suara"><Square className="h-4 w-4 fill-white" /></button>
    </div>
  );
};

export const ChatComposer = ({ onText, onFile, onLocation, disabled, blockedNote, quickReplies, memberName, onManageTemplates }) => {
  const [text, setText] = useState("");
  const [menu, setMenu] = useState(false);
  const [quick, setQuick] = useState(false);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);
  const pdfRef = useRef(null);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const pick = (id) => {
    setMenu(false);
    if (id === "image") imgRef.current?.click();
    if (id === "pdf") pdfRef.current?.click();
    if (id === "audio") {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return toast.error("Perangkat ini belum mendukung rekam suara");
      setRecording(true);
    }
    if (id === "location") {
      run(async () => {
        const loc = await getLocation();
        if (!loc) return toast.error("Lokasi tidak didapat. Izinkan akses lokasi lalu coba lagi.");
        await onLocation(loc);
      });
    }
  };

  const fileChosen = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Ukuran file maksimal 10MB");
    run(() => onFile(f));
  };

  const send = () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    run(() => onText(t));
  };

  if (blockedNote) {
    return <div className="px-4 py-3 text-xs text-center text-slate-600 bg-white/90 border-t border-slate-200" data-testid="chat-blocked-note">{blockedNote}</div>;
  }

  return (
    <div className="relative px-2 py-2 bg-[#f0f2f5] border-t border-slate-200" data-testid="chat-composer">
      {quick && quickReplies && (
        <div className="absolute bottom-full left-2 right-2 mb-2 bg-white rounded-2xl shadow-xl border border-slate-100 max-h-72 overflow-y-auto" data-testid="chat-quick-menu">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 sticky top-0 bg-white">
            <span className="text-xs font-bold text-slate-700">Balasan Cepat</span>
            {onManageTemplates && <button onClick={() => { setQuick(false); onManageTemplates(); }} data-testid="chat-quick-manage" className="text-xs font-semibold text-emerald-700">Kelola</button>}
          </div>
          {quickReplies.length === 0 && <p className="text-xs text-slate-500 p-3">Belum ada template.</p>}
          {quickReplies.map((q) => (
            <button key={q.tpl_id} data-testid={`chat-quick-${q.tpl_id}`}
              onClick={() => { setText(q.text.replaceAll("{nama}", memberName || "Anggota")); setQuick(false); }}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-50">
              <div className="text-sm font-semibold text-slate-800">{q.title}</div>
              <div className="text-[11px] text-slate-500 line-clamp-1">{q.text}</div>
            </button>
          ))}
        </div>
      )}
      {menu && (
        <div className="absolute bottom-full left-2 mb-2 bg-white rounded-2xl shadow-xl border border-slate-100 p-3 grid grid-cols-4 gap-3 w-[300px] max-w-[calc(100%-1rem)]" data-testid="chat-attach-menu">
          {OPTIONS.map(({ id, label, icon: Icon, color }) => (
            <button key={id} onClick={() => pick(id)} data-testid={`chat-attach-${id}`} className="flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-700">
              <span className={`h-12 w-12 rounded-full ${color} text-white flex items-center justify-center shadow-md`}><Icon className="h-5 w-5" /></span>{label}
            </button>
          ))}
        </div>
      )}
      <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={fileChosen} data-testid="chat-image-input" />
      <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={fileChosen} data-testid="chat-pdf-input" />
      <div className="flex items-end gap-2">
        {recording ? (
          <Recorder onCancel={() => setRecording(false)} onDone={(f, d) => { setRecording(false); run(() => onFile(f, d)); }} />
        ) : (
          <>
            <button onClick={() => { setMenu(!menu); setQuick(false); }} disabled={disabled || busy} data-testid="chat-plus-button" aria-label="Lampiran"
              className={`h-11 w-11 shrink-0 rounded-full flex items-center justify-center transition-transform ${menu ? "bg-slate-300 rotate-45" : "bg-white"} text-slate-700`}>
              {menu ? <X className="h-5 w-5 -rotate-45" /> : <Plus className="h-6 w-6" />}
            </button>
            {quickReplies && (
              <button onClick={() => { setQuick(!quick); setMenu(false); }} disabled={disabled || busy} data-testid="chat-quick-button" aria-label="Balasan cepat"
                className={`h-11 w-11 shrink-0 rounded-full flex items-center justify-center ${quick ? "bg-amber-400 text-white" : "bg-white text-amber-500"}`}>
                <Zap className="h-5 w-5" />
              </button>
            )}
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Ketik pesan" disabled={disabled}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              data-testid="chat-input" className="flex-1 resize-none max-h-28 rounded-3xl bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500/40" />
            <button onClick={send} disabled={disabled || busy || !text.trim()} data-testid="chat-send-button" aria-label="Kirim"
              className="h-11 w-11 shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white flex items-center justify-center">
              <SendHorizontal className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
      {busy && <div className="text-[11px] text-center text-slate-500 mt-1" data-testid="chat-sending">Mengirim...</div>}
    </div>
  );
};
